/**
 * MailStatus → display label + color tag.
 *
 * !! KEEP IN SYNC WITH:
 *    apps/service-mail/src/modules/mail-item/mail-item-state-machine.ts
 *    (MailStatus union — REGISTERED / ACCEPTED / IN_TRANSIT / OUT_FOR_DELIVERY
 *    / DELIVERED / RETURNED / LOST / DESTROYED).
 *
 * Server returns `status` as a string — we treat any unknown value as a
 * generic "Unknown" status with a slate tag rather than throwing, so a
 * server-side enum addition doesn't crash the citizen page during the
 * deploy gap.
 */

export type StatusTone = 'blue' | 'amber' | 'emerald' | 'rose' | 'slate';

export interface StatusDisplay {
  label: string;
  tone: StatusTone;
  /** Tailwind-friendly bg/fg classes wired to the tokens in tailwind.css. */
  className: string;
}

const STATUS_DISPLAY: Record<string, StatusDisplay> = {
  REGISTERED: {
    label: 'Registered',
    tone: 'blue',
    className: 'bg-[var(--color-status-blue-bg)] text-[var(--color-status-blue-fg)]',
  },
  ACCEPTED: {
    label: 'Accepted',
    tone: 'blue',
    className: 'bg-[var(--color-status-blue-bg)] text-[var(--color-status-blue-fg)]',
  },
  IN_TRANSIT: {
    label: 'In transit',
    tone: 'amber',
    className: 'bg-[var(--color-status-amber-bg)] text-[var(--color-status-amber-fg)]',
  },
  OUT_FOR_DELIVERY: {
    label: 'Out for delivery',
    tone: 'amber',
    className: 'bg-[var(--color-status-amber-bg)] text-[var(--color-status-amber-fg)]',
  },
  DELIVERED: {
    label: 'Delivered',
    tone: 'emerald',
    className: 'bg-[var(--color-status-emerald-bg)] text-[var(--color-status-emerald-fg)]',
  },
  RETURNED: {
    label: 'Returned to sender',
    tone: 'rose',
    className: 'bg-[var(--color-status-rose-bg)] text-[var(--color-status-rose-fg)]',
  },
  LOST: {
    label: 'Lost',
    tone: 'rose',
    className: 'bg-[var(--color-status-rose-bg)] text-[var(--color-status-rose-fg)]',
  },
  DESTROYED: {
    label: 'Destroyed',
    tone: 'slate',
    className: 'bg-[var(--color-status-slate-bg)] text-[var(--color-status-slate-fg)]',
  },
};

const UNKNOWN_DISPLAY: StatusDisplay = {
  label: 'Unknown',
  tone: 'slate',
  className: 'bg-[var(--color-status-slate-bg)] text-[var(--color-status-slate-fg)]',
};

export function formatStatus(status: string): StatusDisplay {
  return STATUS_DISPLAY[status] ?? UNKNOWN_DISPLAY;
}
